import { useState, useEffect, useCallback } from 'react';
import { notificationService } from '@/services/notification.service';

/**
 * Outcome of a {@link UseDesktopNotificationsReturn.toggleNotifications} call.
 * The caller (NotificationBell) maps each status to a user-facing toast — no
 * outcome is ever silent.
 */
export type NotificationToggleResult =
  | { ok: true; status: 'enabled' }
  | { ok: true; status: 'disabled' }
  | { ok: false; status: 'blocked' }
  | { ok: false; status: 'unsupported' }
  | { ok: false; status: 'error'; message: string };

interface UseDesktopNotificationsReturn {
  isSupported: boolean;
  isPushSupported: boolean;
  permission: NotificationPermission;
  enabled: boolean;
  /** Browser denied/auto-blocked the permission — a prompt won't appear. */
  isBlocked: boolean;
  isPushSubscribed: boolean;
  requestPermission: () => Promise<boolean>;
  toggleNotifications: () => Promise<NotificationToggleResult>;
  showTestNotification: () => void;
}

export function useDesktopNotifications(): UseDesktopNotificationsReturn {
  const [permission, setPermission] = useState<NotificationPermission>(notificationService.permission);
  const [enabled, setEnabled] = useState(notificationService.enabled);
  const [isPushSubscribed, setIsPushSubscribed] = useState(notificationService.isPushSubscribed);
  const [pushInitialized, setPushInitialized] = useState(false);

  useEffect(() => {
    setPermission(notificationService.permission);
    setEnabled(notificationService.enabled);
    setIsPushSubscribed(notificationService.isPushSubscribed);

    const initPush = async () => {
      if (!pushInitialized && notificationService.pushSupported) {
        const success = await notificationService.initPushNotifications();
        setPushInitialized(success);

        if (success && notificationService.enabled) {
          const serverStatus = await notificationService.checkPushStatus();

          if (!serverStatus.subscribed) {
            const subscribed = await notificationService.subscribeToPush();
            setIsPushSubscribed(subscribed);
          } else {
            setIsPushSubscribed(true);
          }
        }
      }
    };

    void initPush();
  }, [pushInitialized]);

  /**
   * Ensure the Web Push layer is initialized (VAPID key fetched) before
   * attempting a subscription. Initializing on-demand removes the silent no-op
   * that happened when `pushInitialized` was still false at click time.
   */
  const ensurePushInitialized = useCallback(async (): Promise<boolean> => {
    if (pushInitialized) return true;
    if (!notificationService.pushSupported) return false;
    const success = await notificationService.initPushNotifications();
    setPushInitialized(success);
    return success;
  }, [pushInitialized]);

  const requestPermission = useCallback(async (): Promise<boolean> => {
    // `Notification.requestPermission()` must be the first await so the user
    // gesture that triggered this call is still in scope (no preceding await).
    const result = await notificationService.requestPermission();
    setPermission(result);
    const isGranted = result === 'granted';
    setEnabled(notificationService.enabled);

    if (isGranted) {
      const initialized = await ensurePushInitialized();
      if (initialized) {
        const subscribed = await notificationService.subscribeToPush();
        setIsPushSubscribed(subscribed);
      }
    }

    return isGranted;
  }, [ensurePushInitialized]);

  const toggleNotifications = useCallback(async (): Promise<NotificationToggleResult> => {
    if (!notificationService.supported) {
      return { ok: false, status: 'unsupported' };
    }

    // Currently active with a live push subscription → turn OFF (drop the push
    // subscription). The OS-level permission cannot be revoked from JS.
    if (enabled && isPushSubscribed) {
      try {
        await notificationService.unsubscribeFromPush();
        setIsPushSubscribed(false);
        setEnabled(notificationService.enabled);
        return { ok: true, status: 'disabled' };
      } catch (error) {
        return { ok: false, status: 'error', message: (error as Error).message };
      }
    }

    // Otherwise we want to ENABLE. Resolve the permission first, preserving the
    // user gesture: the browser permission read is synchronous and the request
    // (if needed) is the first await in this handler.
    const current = notificationService.permission;
    if (current === 'denied') {
      setPermission('denied');
      setEnabled(false);
      return { ok: false, status: 'blocked' };
    }

    if (current !== 'granted') {
      const result = await notificationService.requestPermission();
      setPermission(result);
      if (result !== 'granted') {
        // Either the user dismissed/denied, or Chrome auto-blocked after
        // repeated dismissals — both leave the prompt unavailable.
        setEnabled(notificationService.enabled);
        return { ok: false, status: 'blocked' };
      }
    }

    // Permission granted → best-effort push subscription (init on demand).
    try {
      const initialized = await ensurePushInitialized();
      if (initialized) {
        const subscribed = await notificationService.subscribeToPush();
        setIsPushSubscribed(subscribed);
      }
      setEnabled(notificationService.enabled);
      return { ok: true, status: 'enabled' };
    } catch (error) {
      setEnabled(notificationService.enabled);
      return { ok: false, status: 'error', message: (error as Error).message };
    }
  }, [enabled, isPushSubscribed, ensurePushInitialized]);

  const showTestNotification = useCallback(() => {
    notificationService.show({
      title: 'Test Notifica',
      body: 'Le notifiche desktop sono attive!',
      tag: 'test-notification'
    });
  }, []);

  return {
    isSupported: notificationService.supported,
    isPushSupported: notificationService.pushSupported,
    permission,
    enabled,
    isBlocked: notificationService.supported && permission === 'denied',
    isPushSubscribed,
    requestPermission,
    toggleNotifications,
    showTestNotification
  };
}
